import s from "./TourSection.module.scss";
import { getSectionText } from "../data/sectionTexts";
import { useLanguage } from "../contexts/LanguageContext";
import { homeText } from "../i18n/home";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { tagText } from "../i18n/tag";
import Title from "../Title/Title";
import { tours } from "../../src/data/tours";
import { trackEvent } from "../utils/analytics";

const filters = [
  { key: "all", icon: "grid" },
  { key: "general", icon: "user" },
  { key: "influencer", icon: "star" },
  { key: "creator", icon: "camera" },
];

const SHOW_TOUR_FILTERS = false;

const detailButtonText = {
  JP: "詳細を見る",
  EN: "View details",
  中文: "查看详情",
  한국어: "자세히 보기",
};

const comingSoonText = {
  JP: "準備中",
  EN: "Coming soon",
  中文: "即将推出",
  한국어: "준비 중",
};

const EMPTY_IMAGE =
  'data:image/svg+xml;charset=utf-8,%3Csvg xmlns="http://www.w3.org/2000/svg" width="16" height="12"%3E%3Crect width="16" height="12" fill="%23eef3f8"/%3E%3C/svg%3E';

const CLOUDINARY_UPLOAD_MARKER = "/image/upload/";

const applyCloudinaryTransform = (src, transform) => {
  if (!src || typeof src !== "string") return EMPTY_IMAGE;

  if (!src.includes(CLOUDINARY_UPLOAD_MARKER)) {
    return src;
  }

  const [base, rest] = src.split(CLOUDINARY_UPLOAD_MARKER);
  const versionStart = rest.search(/v\d+\//);
  const cleanRest = versionStart >= 0 ? rest.slice(versionStart) : rest;

  return `${base}${CLOUDINARY_UPLOAD_MARKER}${transform}/${cleanRest}`;
};

const getTourImage = (src, width = 720, height = 520) => {
  return applyCloudinaryTransform(
    src,
    `f_auto,q_auto,w_${width},h_${height},c_fill,g_auto`,
  );
};

const getTourImageSrcSet = (src) => {
  return [
    [420, 300],
    [640, 460],
    [900, 650],
  ]
    .map(([width, height]) => {
      return `${getTourImage(src, width, height)} ${width}w`;
    })
    .join(", ");
};

const TourImage = ({ src, alt, shouldLoad, priority = false }) => {
  const imageSrc = shouldLoad ? getTourImage(src) : EMPTY_IMAGE;
  const imageSrcSet = shouldLoad ? getTourImageSrcSet(src) : undefined;

  return (
    <img
      src={imageSrc}
      srcSet={imageSrcSet}
      sizes="(max-width: 768px) 86vw, 320px"
      alt={alt}
      loading={priority ? "eager" : "lazy"}
      fetchPriority={priority ? "high" : "low"}
      decoding="async"
      width="720"
      height="520"
    />
  );
};

const FilterIcon = ({ type }) => {
  if (type === "grid") {
    return (
      <svg viewBox="0 0 24 24">
        <rect x="4" y="4" width="6" height="6" rx="1.5" />
        <rect x="14" y="4" width="6" height="6" rx="1.5" />
        <rect x="4" y="14" width="6" height="6" rx="1.5" />
        <rect x="14" y="14" width="6" height="6" rx="1.5" />
      </svg>
    );
  }

  if (type === "user") {
    return (
      <svg viewBox="0 0 24 24">
        <circle cx="12" cy="8" r="3.5" />
        <path d="M5 20c.8-4.2 3.2-6.5 7-6.5s6.2 2.3 7 6.5" />
      </svg>
    );
  }

  if (type === "star") {
    return (
      <svg viewBox="0 0 24 24">
        <path d="M12 3.5l2.7 5.5 6.1.9-4.4 4.3 1 6.1L12 17.4l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9L12 3.5z" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24">
      <rect x="4" y="7" width="16" height="12" rx="3" />
      <path d="M8.5 7l1.7-2h3.6l1.7 2" />
      <circle cx="12" cy="13" r="3.2" />
    </svg>
  );
};

const DurationIcon = () => (
  <svg viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

const StartTimeIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M7 3v3" />
    <path d="M17 3v3" />
    <rect x="4" y="5" width="16" height="16" rx="3" />
    <path d="M4 10h16" />
  </svg>
);

const PeopleIcon = () => (
  <svg viewBox="0 0 24 24">
    <circle cx="9" cy="8" r="3" />
    <path d="M3.5 20c.6-4 2.7-6 5.5-6s4.9 2 5.5 6" />
    <circle cx="17" cy="9" r="2.5" />
  </svg>
);

const DroneTagIcon = () => (
  <svg viewBox="0 0 24 24">
    <circle cx="7" cy="7" r="2.5" />
    <circle cx="17" cy="7" r="2.5" />
    <circle cx="7" cy="17" r="2.5" />
    <circle cx="17" cy="17" r="2.5" />
    <path d="M9 9l6 6" />
    <path d="M15 9l-6 6" />
  </svg>
);

const SecretSpotTagIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M12 21s7-6.2 7-12a7 7 0 10-14 0c0 5.8 7 12 7 12z" />
    <path d="M9.5 10.5l1.7 1.7 3.5-3.5" />
  </svg>
);

const CameraTagIcon = () => (
  <svg viewBox="0 0 24 24">
    <rect x="3" y="7" width="18" height="12" rx="3" />
    <path d="M8 7l1.5-2h5L16 7" />
    <circle cx="12" cy="13" r="3.5" />
  </svg>
);

const LocationTagIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M12 21s6-5.3 6-11a6 6 0 10-12 0c0 5.7 6 11 6 11z" />
    <circle cx="12" cy="10" r="2.2" />
  </svg>
);

const ModelTagIcon = () => (
  <svg viewBox="0 0 24 24">
    <circle cx="12" cy="8" r="3.2" />
    <path d="M5 20c1-4 3.5-6 7-6s6 2 7 6" />
  </svg>
);

const PickupTagIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M3 16V9h12l3 4h3v3" />
    <circle cx="7" cy="17" r="2" />
    <circle cx="17" cy="17" r="2" />
  </svg>
);

const PrivateTagIcon = () => (
  <svg viewBox="0 0 24 24">
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 118 0v3" />
  </svg>
);

const StarrySkyTagIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path
      d="M12 2L13.8 7.2L19 9L13.8 10.8L12 16L10.2 10.8L5 9L10.2 7.2L12 2Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BoatTagIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path
      d="M4 14L12 6L20 14H4Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M3 17C4.5 18.5 6 18.5 7.5 17C9 15.5 10.5 15.5 12 17C13.5 18.5 15 18.5 16.5 17C18 15.5 19.5 15.5 21 17"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const SnorkelTagIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path
      d="M9 13C9 10.7909 10.7909 9 13 9H14"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M14 9V5C14 4.44772 14.4477 4 15 4H17C17.5523 4 18 4.44772 18 5V12"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6 14C6 17.3137 8.68629 20 12 20H13"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <circle cx="13" cy="14" r="3" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const TurtleSnorkelTagIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <ellipse
      cx="12"
      cy="12"
      rx="4.5"
      ry="3.8"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <circle cx="17.8" cy="12" r="1.3" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M9.5 8.8L8 7.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M9.5 15.2L8 16.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M14.5 8.8L16 7.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M14.5 15.2L16 16.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M7.5 12H6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M20 10V5.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M20 5.5H22"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const SupIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path
      d="M5 13L12 10L19 13"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 10V16"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <circle cx="12" cy="8" r="2" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M16 4L18 14"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M3 19C4.5 17.5 6 17.5 7.5 19C9 20.5 10.5 20.5 12 19C13.5 17.5 15 17.5 16.5 19C18 20.5 19.5 20.5 21 19"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const EmptyHandTagIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <rect
      x="5"
      y="8"
      width="10"
      height="11"
      rx="2"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path
      d="M8 8V6.8A2 2 0 0110 5h0a2 2 0 012 2V8"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M16.5 15l1.8 1.8L21 13.8"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SurfingTagIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path
      d="M5 15C7.5 12.5 10.5 12.5 13 15C15.5 17.5 18.5 17.5 21 15"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M4 19C6 17.8 8 17.8 10 19C12 20.2 14 20.2 16 19C18 17.8 20 17.8 22 19"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M7 11C10.5 8.2 14.5 7.5 18.5 9"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M12 6L14 8.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <circle cx="11" cy="4.5" r="1.6" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const JetTagIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path
      d="M4 15H13.5C15.9 15 18 13.6 19 11.5L19.5 10.5H15L13 7.5H9L10.5 10.5H7.8C5.7 10.5 4 12.2 4 14.3V15Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12.5 7.5L15.3 5.3"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M4 18C5.5 17 7 17 8.5 18C10 19 11.5 19 13 18C14.5 17 16 17 17.5 18C19 19 20.5 19 22 18"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const UnderwaterCameraTagIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <rect
      x="4"
      y="6"
      width="16"
      height="10"
      rx="2.5"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path
      d="M8 6l1.5-2h5L16 6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="11" r="2.8" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M4 19c1-.8 2-.8 3 0s2 .8 3 0 2-.8 3 0 2 .8 3 0 2-.8 3 0"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const TagIconMap = {
  drone: <DroneTagIcon />,
  camera: <CameraTagIcon />,
  underwater: <UnderwaterCameraTagIcon />,
  location: <LocationTagIcon />,
  model: <ModelTagIcon />,
  pickup: <PickupTagIcon />,
  private: <PrivateTagIcon />,
  secret: <SecretSpotTagIcon />,
  starry: <StarrySkyTagIcon />,
  boat: <BoatTagIcon />,
  snorkel: <SnorkelTagIcon />,
  turtleSnorkel: <TurtleSnorkelTagIcon />,
  sup: <SupIcon />,
  emptyHand: <EmptyHandTagIcon />,
  surfing: <SurfingTagIcon />,
  jet: <JetTagIcon />,
};

const TourCard = ({
  tour,
  tags,
  isTallCard,
  detailLabel,
  comingSoonLabel,
  priority = false,
}) => {
  const navigate = useNavigate();
  const sliderRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadAllImages, setLoadAllImages] = useState(false);

  const images = Array.isArray(tour.images) ? tour.images : [];
  const tourTags = Array.isArray(tour.tags) ? tour.tags : [];
  const startTimes = Array.isArray(tour.startTimes)
    ? tour.startTimes.join(" / ")
    : tour.startTimes || "";

  const salePriceParts = String(tour.salePrice || "").split(" / ");
  const saleMain = salePriceParts[0] || "";
  const saleUnit = salePriceParts[1] || "";

  const handleCardClick = () => {
    if (!tour.href || tour.isComingSoon) return;

    trackEvent("tour_card_click", {
      event_category: "tour",
      event_label: tour.title,
      tour_id: tour.id,
      tour_title: tour.title,
      destination: tour.href,
      placement: "tour_section_card",
    });

    navigate(tour.href);
  };

  const handleCardKeyDown = (e) => {
    if (!tour.href || tour.isComingSoon) return;

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      navigate(tour.href);
    }
  };

  const updateImageIndex = () => {
    if (!sliderRef.current) return;

    const { scrollLeft, clientWidth } = sliderRef.current;
    const index = Math.round(scrollLeft / clientWidth);

    setCurrentIndex(index);
  };

  const scrollSlider = (direction) => {
    if (!sliderRef.current || images.length === 0) return;

    const newIndex =
      direction === "left"
        ? Math.max(currentIndex - 1, 0)
        : Math.min(currentIndex + 1, images.length - 1);

    setLoadAllImages(true);

    sliderRef.current.scrollTo({
      left: newIndex * sliderRef.current.clientWidth,
      behavior: "smooth",
    });

    setCurrentIndex(newIndex);
  };

  const handleImageSliderWheel = (e) => {
    if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;

    e.preventDefault();
    window.scrollBy({
      top: e.deltaY,
      left: 0,
      behavior: "auto",
    });
  };

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    slider.addEventListener("scroll", updateImageIndex, { passive: true });

    return () => {
      slider.removeEventListener("scroll", updateImageIndex);
    };
  }, []);

  return (
    <article
      className={`${s.card} ${isTallCard ? s.tallCard : ""}`}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role={tour.href && !tour.isComingSoon ? "link" : undefined}
      tabIndex={tour.href && !tour.isComingSoon ? 0 : undefined}
      onPointerEnter={() => setLoadAllImages(true)}
      onTouchStart={() => setLoadAllImages(true)}
    >
      <div className={s.imageWrapper}>
        <div
          className={s.imageSlider}
          ref={sliderRef}
          onWheel={handleImageSliderWheel}
        >
          {images.map((image, index) => (
            <TourImage
              key={`${tour.id || tour.title}-${index}`}
              src={image}
              alt={`${tour.title} 写真 ${index + 1}`}
              shouldLoad={index === 0 || loadAllImages}
              priority={priority && index === 0}
            />
          ))}
        </div>

        {tour.tag && <div className={s.badge}>{tour.tag}</div>}

        {images.length > 1 && (
          <>
            <button
              type="button"
              className={`${s.arrow} ${s.left}`}
              onClick={(e) => {
                e.stopPropagation();
                scrollSlider("left");
              }}
              aria-label="前の写真を見る"
            >
              <svg viewBox="0 0 24 24">
                <path d="M15 6l-6 6 6 6" />
              </svg>
            </button>

            <button
              type="button"
              className={`${s.arrow} ${s.right}`}
              onClick={(e) => {
                e.stopPropagation();
                scrollSlider("right");
              }}
              aria-label="次の写真を見る"
            >
              <svg viewBox="0 0 24 24">
                <path d="M9 6l6 6-6 6" />
              </svg>
            </button>

            <div className={s.dots}>
              {images.map((_, index) => (
                <span
                  key={index}
                  className={currentIndex === index ? s.activeDot : ""}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className={s.content}>
        <h3>{tour.title}</h3>

        <div className={s.tagList}>
          {tourTags.map((tag) => (
            <span key={tag}>
              {TagIconMap[tag] || <CameraTagIcon />}
              {tags[tag]}
            </span>
          ))}
        </div>

        <div className={s.meta}>
          <div className={s.metaItem}>
            <div className={s.metaIcon}>
              <DurationIcon />
            </div>

            <div className={s.metaText}>
              <span>{tour.time}</span>
            </div>
          </div>

          <div className={s.metaItem}>
            <div className={s.metaIcon}>
              <StartTimeIcon />
            </div>

            <div className={s.metaText}>
              <span>{startTimes}</span>
            </div>
          </div>

          <div className={s.metaItem}>
            <div className={s.metaIcon}>
              <PeopleIcon />
            </div>

            <div className={s.metaText}>
              <span>{tour.people}</span>
            </div>
          </div>
        </div>

        {tour.notes && <div className={s.notes}>{tour.notes}</div>}

        <div className={s.priceArea}>
          <div className={s.priceText}>
            <div className={s.originalRow}>
              {tour.originalPrice && (
                <span className={s.originalPrice}>{tour.originalPrice}</span>
              )}

              {tour.discountRate && (
                <span className={s.discountRate}>{tour.discountRate}</span>
              )}
            </div>

            <span className={s.salePrice}>
              <strong>{saleMain}</strong>

              {saleUnit && <small>/{saleUnit}</small>}
            </span>
          </div>

          <button
            type="button"
            className={`${s.detailBtn} ${tour.isComingSoon ? s.comingSoonBtn : ""}`}
            disabled={tour.isComingSoon}
            onClick={(e) => {
              e.stopPropagation();
              handleCardClick();
            }}
          >
            {tour.isComingSoon
              ? comingSoonLabel
              : detailLabel}
            {!tour.isComingSoon && (
              <svg viewBox="0 0 24 24">
                <path d="M9 6l6 6-6 6" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </article>
  );
};

const TourSection = ({
  activeFilter: controlledActiveFilter,
  onChangeFilter,
}) => {
  const { lang } = useLanguage();
  const t = homeText[lang];
  const titleText = getSectionText(lang, "tours");
  const tourList = tours[lang] || tours.JP;
  const tags = tagText[lang];

  const [internalActiveFilter, setInternalActiveFilter] = useState("all");

  const activeFilter = controlledActiveFilter || internalActiveFilter;

  const handleFilterChange = (filterKey) => {
    trackEvent("tour_filter_click", {
      event_category: "tour",
      event_label: filterKey,
      filter_key: filterKey,
      placement: "tour_section",
    });

    if (onChangeFilter) {
      onChangeFilter(filterKey);
      return;
    }

    setInternalActiveFilter(filterKey);
  };

  const [canScroll, setCanScroll] = useState(false);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  const gridRef = useRef(null);

  const filteredTours =
    activeFilter === "all"
      ? tourList
      : tourList.filter((tour) => tour.category === t[activeFilter]);

  const updateScrollState = () => {
    if (!gridRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = gridRef.current;

    setCanScroll(scrollWidth > clientWidth);
    setIsAtStart(scrollLeft <= 4);
    setIsAtEnd(scrollLeft + clientWidth >= scrollWidth - 4);
  };

  const scrollCards = (direction) => {
    gridRef.current?.scrollBy({
      left: direction === "left" ? -360 : 360,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    updateScrollState();

    const grid = gridRef.current;
    if (!grid) return;

    grid.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);

    return () => {
      grid.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [filteredTours]);

  return (
    <section className={s.root} id="tour-section">
      <Title kicker={titleText.kicker} title={titleText.title} />

      {SHOW_TOUR_FILTERS && (
        <div className={s.filterRow}>
          <div className={s.filters}>
            {filters.map((item) => (
              <button
                key={item.key}
                type="button"
                className={activeFilter === item.key ? s.active : ""}
                onClick={() => handleFilterChange(item.key)}
              >
                <span className={s.filterIcon}>
                  <FilterIcon type={item.icon} />
                </span>
                {t[item.key]}
              </button>
            ))}
          </div>

          {canScroll && (
            <div className={s.cardControls}>
              <button
                type="button"
                className={isAtStart ? s.disabled : ""}
                disabled={isAtStart}
                onClick={() => scrollCards("left")}
                aria-label="前の記事を見る"
              >
                <svg viewBox="0 0 24 24">
                  <path d="M15 6l-6 6 6 6" />
                </svg>
              </button>

              <button
                type="button"
                className={isAtEnd ? s.disabled : ""}
                disabled={isAtEnd}
                onClick={() => scrollCards("right")}
                aria-label="次の記事を見る"
              >
                <svg viewBox="0 0 24 24">
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </button>
            </div>
          )}
        </div>
      )}

      <div className={s.gridWrapper}>
        <div
          className={`${s.grid} ${filteredTours.length <= 3 ? s.compactGrid : ""}`}
          ref={gridRef}
        >
          {filteredTours.map((tour, index) => (
            <TourCard
              key={tour.id || tour.title}
              tour={tour}
              tags={tags}
              isTallCard={tour.category === t.influencer}
              detailLabel={detailButtonText[lang] || detailButtonText.JP}
              comingSoonLabel={comingSoonText[lang] || comingSoonText.JP}
              priority={index === 0}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TourSection;
